// StallHeader.tsx
import React, { useEffect, useState } from 'react';
import './StallHeader.scss';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import LanguageIcon from '@material-ui/icons/Language';
import { http, general, formatService, localstorage, AlertService } from '../../fx-core';
import dummyImage from "./../../../assets/images/dummy.png";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import clicking from '../../../assets/clicking.svg';
import {
  Drawer,
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Divider,
  Grid,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Avatar
} from '@material-ui/core';
import MessageContainer from '../../MessageContainer/MessageContainer';
import { useSelector } from 'react-redux';
import LoginContainer from '../../LoginContainer/LoginContainer';
import verifyTick from "./../../../assets/img/verifytick.png";
import notliked from '../../../assets/notliked.png';
import liked from '../../../assets/liked.png';
import { Star, StarBorder, Report, DeleteOutline } from '@material-ui/icons';
import { jwtDecode } from 'jwt-decode';

// ---- Review Report Reasons ----
const ReviewReportReasons = [
  "Spam or misleading",
  "Hate speech",
  "Abusive or harassing",
  "Other"
];

// ---- StallReviewsSidebar ----
type StallReviewsSidebarProps = {
  open: boolean;
  onClose: () => void;
  stallId: number;
  isAdmin: boolean;
  onReportClick: (reviewId: number) => void;
  onDeleteClick: (reviewId: number) => void;
  reportedReviews: Set<number>;
};

const StallReviewsSidebar: React.FC<StallReviewsSidebarProps> = ({
  open,
  onClose,
  stallId,
  isAdmin,
  onReportClick,
  onDeleteClick,
  reportedReviews
}) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && stallId) {
      setLoading(true);
      http.get({ url: `stallreviews/stall/${stallId}` })
        .then(res => setReviews(res.data.reviews || []))
        .catch(() => setReviews([]))
        .finally(() => setLoading(false));
    } else {
      setReviews([]);
    }
  }, [open, stallId]);

  const renderStars = (rating: number) => (
    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {[...Array(5)].map((_, i) =>
        rating >= i + 1
          ? <Star key={i} fontSize="small" style={{ color: "#FFD700" }} />
          : rating >= i + 0.5
            ? <Star key={i} fontSize="small" style={{ color: "#FFD700", opacity: 0.6 }} />
            : <StarBorder key={i} fontSize="small" style={{ color: "#BDBDBD" }} />
      )}
    </span>
  );

  // --- Reviewer Name & Avatar Helpers ---
  const getReviewerName = (r: any) => {
    return r.user?.name
      || r.userName
      || r.reviewerName
      || r.createdBy
      || r.displayName
      || r.name
      || "Anonymous";
  };

  const getInitials = (name: string) => {
    if (!name) return "A";
    return name
      .split(" ")
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ style: { width: '600px', maxWidth: '92vw', background: "#fafbfc", minHeight: "100vh", boxSizing: "border-box", padding: 0 } }}
      style={{ zIndex: 1200 }}
    >
      <div>
        <div style={{ background: "#FF9800", padding: '20px 24px', display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>Stall Reviews</Typography>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", cursor: 'pointer' }}>
            <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>&times;</span>
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {loading && <div style={{ textAlign: "center", marginTop: 40 }}>Loading...</div>}
          {!loading && reviews.length === 0 && <div style={{ color: "#666", marginTop: 40, textAlign: "center" }}>No reviews yet.</div>}

          {reviews.map((rev) => (
            <Box key={rev.id} mb={2} p={2} bgcolor="#fff" borderRadius={6} boxShadow="0 1px 4px rgba(0,0,0,0.06)">
  <Grid container spacing={1} alignItems="flex-start">

    {/* Avatar */}
    <Grid item>
      <Avatar style={{ width: 40, height: 40, backgroundColor: "#80b933",color: "#fff", fontWeight: 700 }}>
        {getInitials(rev.userName)}
      </Avatar>
    </Grid>

    {/* Main content */}
    <Grid item xs>
      {/* Name */}
      <Typography style={{ fontWeight: 700, fontSize: 16 }}>
        {rev.userName}
      </Typography>

      {/* Rating + stars */}
      <Box display="flex" alignItems="center" mt={0.5}>
        <Typography style={{ fontWeight: 700, fontSize: 18, marginRight: 8 }}>
          {rev.rating.toFixed(1)}
        </Typography>
        {renderStars(rev.rating)}
      </Box>

      {/* Title (bold if present) */}
      {rev.title && (
        <Typography style={{ fontSize: 15, fontWeight: 600, color: "#222", marginTop: 6 }}>
          {rev.title}
        </Typography>
      )}

      {/* Description — only once */}
      <Typography style={{ fontSize: 14, color: "#444", marginTop: 6 }}>
        {rev.description}
      </Typography>
    </Grid>

    {/* Actions column — keeps Report/Delete at top and Date at bottom */}
    <Grid item style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Button
          size="small"
          onClick={() => onReportClick(rev.id)}
          startIcon={<Report />}
          disabled={reportedReviews.has(rev.id)}
          title={reportedReviews.has(rev.id) ? "You reported this" : "Report"}
        >
          {reportedReviews.has(rev.id) ? "Reported" : "Report"}
        </Button>

        {isAdmin && (
          <IconButton aria-label="delete" size="small" onClick={() => onDeleteClick(rev.id)} style={{ color: '#777' }}>
            <DeleteOutline />
          </IconButton>
        )}
      </div>

      {/* Date aligned bottom-right (parallel to report above) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 60 }}>
        <Typography style={{ color: "#999", fontSize: 12 }}>
          {rev.createdDate ? new Date(rev.createdDate).toLocaleDateString() : ''}
        </Typography>
      </div>
    </Grid>

  </Grid>
</Box>

          ))}
        </div>
      </div>
    </Drawer>
  );
};

// ---- Styles ----
const useStyles = makeStyles((theme) => ({
  drawerWidth: {
    [theme.breakpoints.down(599)]: { width: '90%' },
    [theme.breakpoints.up(600)]: { width: '70%' },
    [theme.breakpoints.up(1025)]: { width: '40%' }
  }
}));

// ---- StallHeader (parent) ----
const StallHeader = (props: any) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const defaultItem = {
    name: "",
    website: "",
    address: "",
    stallOpenStatus: "",
    providerMobile: "",
    providerEmail: "",
    isDeliveryEnabled: false,
    isPickupEnabled: false,
    imageUrl: "",
    id: 0,
    isVerified: false
  };
  const defaultFollowItem = { userId: 0, id: 0, entityId: 0, entityTypeId: 0, isFollow: false };

  const login = useSelector<any, any>(s => s.login);
  const [item, setItem] = useState({ stall: defaultItem, categories: [] } as any);
  const [followItem, setFollowItem] = useState(defaultFollowItem as any);
  const [qrcode, setQrcode] = useState<any>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [openMessage, setOpenMessage] = useState(false);
  const [userId, setUserId] = useState(localstorage.getItem("userId") ? parseInt(localstorage.getItem("userId")) : 0);
  const [openLogin, setOpenLogin] = useState(false);
  const [currentContext, setCurrentContext] = useState({ followedUsers: 0 });
  const [stallbanner, setStallBanner] = useState<any[]>([]);

  const [reviewsSummary, setReviewsSummary] = useState<{ rating: number, count: number }>({ rating: 0, count: 0 });
  const [reviewsSidebarOpen, setReviewsSidebarOpen] = useState(false);

  // Dialog & report state
  const [reportDialog, setReportDialog] = useState<{ open: boolean, reviewId: number | null }>({ open: false, reviewId: null });
  const [reportReason, setReportReason] = useState<string>('');
  const [customMsg, setCustomMsg] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, reviewId: number | null }>({ open: false, reviewId: null });

  // admin detection state
  const [decodedPayload, setDecodedPayload] = useState<any>(null);
  const [tokenPresent, setTokenPresent] = useState<boolean>(false);
  const [isAdminComputed, setIsAdminComputed] = useState<boolean>(false);

  // Keep track of reviews the current user has reported (client session)
  const [reportedReviews, setReportedReviews] = useState<Set<number>>(new Set());

  // Robust admin detection (uses local key `ltk` and userRoles)
  const computeAdminFromToken = () => {
    let computed = false;
    let decoded: any = null;
    try {
      const token = localstorage.getItem('ltk');
      setTokenPresent(!!token);

      if (token) {
        try {
          decoded = typeof jwtDecode === 'function' ? (jwtDecode as any)(token) : JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
          try { decoded = JSON.parse(atob(token.split('.')[1])); } catch { decoded = null; }
        }
      }

      setDecodedPayload(decoded);

      // roles may also live in localStorage as userRoles
      const rolesRaw = localstorage.getItem('userRoles');
      let roles: string[] = [];
      try { roles = rolesRaw ? JSON.parse(rolesRaw) : []; } catch (e) { roles = []; }

      const localUserId = Number(localstorage.getItem('userId')) || NaN;

      const idBasedAdmin = localUserId === 11373 || localUserId === 11374;
      const roleBasedAdmin = roles.includes('ZONAL_ADMIN') || roles.includes('ADMIN') || roles.includes('SUPER_ADMIN')
        || (decoded && (decoded.roleCode === 'ADMIN' || decoded.roleCode === 'ZONAL_ADMIN' || decoded.roles?.includes?.('ADMIN')));

      // Require the redux login flag to be true to consider user 'logged in'
      const isLoggedIn = !!(login && login.isLogin);
      computed = isLoggedIn && (idBasedAdmin || roleBasedAdmin);
    } catch (e) {
      computed = false;
      setDecodedPayload(null);
      setTokenPresent(false);
    }

    setIsAdminComputed(computed);
    return computed;
  };

  useEffect(() => { computeAdminFromToken(); /* eslint-disable-next-line */ }, [login]);

  const isAdmin = isAdminComputed;

  const toggleDrawerMessage = () => setOpenMessage(!openMessage);
  const toggleDrawerLogin = () => setOpenLogin(!openLogin);

  const getStallDetailsById = () => {
    if (!props.stallId) return;
    const inputData = { stallId: props.stallId, currentDate: formatService.getCurrentDateForServer() };
    const apiOptions: any = { url: `portal/stall/${props.stallId}/withcategory/serviceIds`, data: inputData };
    http.post(apiOptions).then(response => {
      const result = response.data;
      result.stall.imageUrl = general.getImageUrl(result.stall.imagePath);
      setItem(result);
      setLinkUrl(result.linkUrl);
      if (props.callBackData) props.callBackData({ linkUrl: result.linkUrl });
    }).catch(()=>{});
  };

  const getStallQrCode = () => {
    if (!props.stallId) return;
    const apiOptions: any = { url: `portal/stall/${props.stallId}/qrcode`, method: 'get' };
    apiOptions.callback = viewImage;
    http.view(apiOptions);
  };
  const viewImage = (res: any) => setQrcode(res.base64data);

  const navToMessage = () => {
    if (login.isLogin && userId) setOpenMessage(true);
    else { AlertService.showMsg({ message: "Please login to chat" }); navToLogin(); }
  };
  const navToLogin = () => setOpenLogin(true);
  const handleLoginClose = () => setOpenLogin(false);
  const onLoginCallBack = () => handleLoginClose();
  const handleMessageClose = () => setOpenMessage(false);

  const getFollowInfoByUserId = () => {
    let loggedInUserId = userId;
    if (login.isLogin) {
      loggedInUserId = parseInt(localstorage.getItem("userId"));
      setUserId(loggedInUserId);
    }
    const inputData = { userId: loggedInUserId > 0 ? loggedInUserId : 0, entityId: props.stallId };
    const apiOptions: any = { url: `portal/follow/query`, data: inputData };
    http.post(apiOptions).then(response => {
      const result = response.data;
      if (result && result.follow) {
        let folloObj = result.follow;
        folloObj.isFollow = folloObj.isFollow > 0 ? true : false;
        setFollowItem(folloObj);
      }
      const data = { followedUsers: result.followedUsers };
      setCurrentContext(prevState => ({ ...prevState, ...data }));
    }).catch(()=>{});
  };

  const addFollow = () => { followItem.entityId = props.stallId; const apiOptions: any = { url: `follow/add`, data: followItem }; http.post(apiOptions).then(() => saveFollowCallBack()).catch(()=>{}); };
  const followStall = () => { const apiOptions: any = { url: `follow/${followItem.id}/updatefollow`, data: null }; http.put(apiOptions).then(() => saveFollowCallBack()).catch(()=>{}); };
  const unFollowStall = () => { const apiOptions: any = { url: `follow/${followItem.id}/updateunfollow`, data: null }; http.put(apiOptions).then(() => saveFollowCallBack()).catch(()=>{}); };
  const saveFollowCallBack = () => getFollowInfoByUserId();

  const saveFollow = () => {
    if (login.isLogin && userId) {
      if (followItem.id === 0) addFollow();
      else if (followItem.id > 0) followItem.isFollow ? unFollowStall() : followStall();
    } else { AlertService.showMsg({ message: "Please login to follow" }); navToLogin(); }
  };

  const openStallWebsite = () => { if (item && item.stall && item.stall.website) window.open(item.stall.website, '_blank'); };

  useEffect(() => {
    if (props.stallId) {
      http.get({ url: `stallreviews/stall/${props.stallId}` })
        .then(res => {
          const reviews = res.data.reviews || [];
          let avg = 0;
          if (reviews.length) { avg = reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length; }
          setReviewsSummary({ rating: reviews.length ? avg : 5.0, count: reviews.length || 0 });
        }).catch(()=>{});
    }
  }, [props.stallId]);

  useEffect(() => {
    getStallDetailsById();
    getStallQrCode();
    getFollowInfoByUserId();
    computeAdminFromToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  // Dialog handlers
  const openReportDialog = (reviewId: number) => {
    // If user already reported (client session) show message
    if (reportedReviews.has(reviewId)) {
      AlertService.showMsg({ message: "You have already reported this review" });
      return;
    }
    setReportDialog({ open: true, reviewId });
    setReportReason('');
    setCustomMsg('');
  };
  const closeReportDialog = () => { setReportDialog({ open: false, reviewId: null }); setReportReason(''); setCustomMsg(''); };

  // ---------------------------
  // Single robust report submit
  // ---------------------------
 const handleReportSubmit = () => {
  if (!reportReason || (reportReason === 'Other' && !customMsg.trim())) {
    AlertService.showMsg({ message: "Please specify a reason" });
    return;
  }
  if (!reportDialog.reviewId) {
    AlertService.showMsg({ message: "Invalid review" });
    return;
  }

  // Prefer redux login state + local userId, fallback to localStorage
  const loggedInUserId = (login && login.isLogin)
    ? (userId && userId > 0 ? userId : (parseInt(localstorage.getItem("userId") || "0", 10)))
    : 0;

  if (!loggedInUserId || isNaN(loggedInUserId) || loggedInUserId <= 0) {
    AlertService.showMsg({ message: "Please login to report" });
    // open the login dialog so the user can login and try again
    setOpenLogin(true);
    return;
  }

  const payload = {
    userId: loggedInUserId, // required by server
    reason: reportReason === "Other" ? customMsg.trim() : reportReason
  };

  // POST with userId in body
  http.post({ url: `stallreviews/${reportDialog.reviewId}/report`, data: payload })
    .then((res: any) => {
      AlertService.showSuccessMsg("Reported. Thank you!");
      // mark locally as reported so UI disables the Report button
      setReportedReviews(prev => {
        const next = new Set(prev);
        next.add(reportDialog.reviewId as number);
        return next;
      });
      // refresh reviews summary (count / avg)
      http.get({ url: `stallreviews/stall/${props.stallId}` })
        .then(res2 => {
          const reviews = res2.data.reviews || [];
          let avg = 0;
          if (reviews.length) { avg = reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length; }
          setReviewsSummary({ rating: reviews.length ? avg : 0.0, count: reviews.length || 0 });
        }).catch(()=>{});
      closeReportDialog();
    })
    .catch((err: any) => {
      // Handle validation errors (400) and show server message if present
      const serverMessage = err && err.response && err.response.data && (err.response.data.error || err.response.data.message);
      
      // NEW: If this is the "already reported" error, also mark it locally
      if (serverMessage && serverMessage.toLowerCase().includes('already reported')) {
        setReportedReviews(prev => {
          const next = new Set(prev);
          next.add(reportDialog.reviewId as number);
          return next;
        });
        closeReportDialog(); // Close dialog since it's already reported
      }
      
      // Show the server error message
      if (serverMessage) {
        AlertService.showMsg({ message: serverMessage });
      } else {
        AlertService.showMsg({ message: "Failed to submit report" });
      }
    });
};

  const openDeleteDialog = (reviewId: number) => {
    // only allow opening delete when user is actually logged-in admin
    const isLoggedIn = !!(login && login.isLogin);
    if (!isLoggedIn) {
      AlertService.showMsg({ message: 'Please login as admin to delete reviews' });
      return;
    }
    if (!isAdmin) {
      AlertService.showMsg({ message: 'Not authorized to delete' });
      return;
    }
    setDeleteDialog({ open: true, reviewId });
  };
  const closeDeleteDialog = () => setDeleteDialog({ open: false, reviewId: null });

  const handleDeleteReview = () => {
    // server-side should still enforce auth; client double-checks user is logged-in admin
    const isLoggedIn = !!(login && login.isLogin);
    if (!isLoggedIn) {
      AlertService.showMsg({ message: 'Please login as admin to delete reviews' });
      closeDeleteDialog();
      return;
    }
    if (!isAdmin) {
      AlertService.showMsg({ message: 'Not authorized to delete' });
      closeDeleteDialog();
      return;
    }

    if (!deleteDialog.reviewId) { AlertService.showMsg({ message: "Invalid review" }); return; }
    http.delete({ url: `stallreviews/${deleteDialog.reviewId}` }).then(() => {
      AlertService.showSuccessMsg("Review deleted.");
      http.get({ url: `stallreviews/stall/${props.stallId}` })
        .then(res => {
          const reviews = res.data.reviews || [];
          let avg = 0;
          if (reviews.length) { avg = reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length; }
          setReviewsSummary({ rating: reviews.length ? avg : 0.0, count: reviews.length || 0 });
        }).catch(()=>{});
      closeDeleteDialog();
    }).catch(() => { AlertService.showMsg({ message: "Failed to delete review" }); });
  };

  const renderReviewSummaryBar = () => (
    <Box display="flex" alignItems="center" gap={1} mt={1} mb={1}>
      <Typography style={{ fontSize: 20, fontWeight: 700, color: reviewsSummary.count > 0 ? '#222' : '#bbb', minWidth: 40 }}>
        {reviewsSummary.count > 0 ? reviewsSummary.rating.toFixed(1) : '5.0'}

      </Typography>

      <Box display="flex" alignItems="center">
        {[...Array(5)].map((_, idx) =>
          reviewsSummary.count > 0
            ? (
              reviewsSummary.rating >= idx + 1
                ? <Star key={idx} style={{ color: '#FFD700', marginLeft: 2, marginRight: 2 }} />
                : reviewsSummary.rating >= idx + 0.5
                  ? <Star key={idx} style={{ color: '#FFD700', opacity: 0.6, marginLeft: 2, marginRight: 2 }} />
                  : <StarBorder key={idx} style={{ color: '#BDBDBD', marginLeft: 2, marginRight: 2 }} />
            )
            : <StarBorder key={idx} style={{ color: '#bbb', marginLeft: 2, marginRight: 2 }} />
        )}
      </Box>

      <Typography style={{ marginLeft: 8, color: reviewsSummary.count > 0 ? '#666' : '#bbb' }}>{`(${reviewsSummary.count || 0})`}</Typography>

      <a href="#" style={{ marginLeft: 12, textDecoration: "underline", color: "#1976d2", fontWeight: 500 }} onClick={(e) => { e.preventDefault(); setReviewsSidebarOpen(true); }}>
        See reviews
      </a>
    </Box>
  );

  function handleShareSlug(slug?: string) {
    if (!slug) { AlertService.showMsg({ message: "No link available to share" }); return; }
    if (navigator.share) { navigator.share({ title: 'Check this out!', text: 'Awesome website!', url: `https://go-localites-in.app.link/stall?s=${slug}` }).catch(()=>{}); }
    else { try { navigator.clipboard.writeText(`https://go-localites-in.app.link/stall?s=${slug}`); AlertService.showSuccessMsg("Link copied to clipboard"); } catch { window.open(`https://go-localites-in.app.link/stall?s=${slug}`, '_blank'); } }
  }

  return (
    <div className="pt-4 stallheader-ui">
      {/* Desktop view */}
      <div className="row deskview pb-2">
        <div className="pl-3">
          {!item.stall.imageUrl ? <img src={dummyImage} alt="Item" width="150" height="150" className="itemimage" /> : <img src={item.stall.imageUrl} alt="Item" width="150" height="150" className="itemimage" />}
        </div>

        <div className='pl-3'>
          {Array.isArray(stallbanner) && stallbanner.length > 0 && stallbanner.map((it, index) => (
            <img src={it.imageUrl} alt="Item" width="150" height="150" className="itemimage" key={index} />
          ))}
        </div>

        <div className="d-flex flex-column justify-content-between pl-4 ml-2 pb-2 col-6">
          <div className="d-flex">
            <span className='font-30'><b>{item.stall.name}</b>
              {item.stall.isVerified && <span className='ml-1'><img src={verifyTick} width="20" height="20" /></span>}
              <span className="fans-style ml-2"><img src={liked} height="15" className='pr-1' />{currentContext.followedUsers} True Fans</span>
            </span>
          </div>

          <div>
            <div className="font-15 addresscolor">{item.categories && item.categories.length > 0 && item.categories.map((category: any, i: number) => <span key={i} className="pr-2">{category}</span>)}</div>
            <div className="font-15 addresscolor">{item.stall.address}</div>
          </div>

          {renderReviewSummaryBar()}

          <div className="row justify-content-between my-3 ml-1">
            {item.stall.website ? (
              <div className="text-center col-2 stall-info-card py-1 pointer" onClick={openStallWebsite}><i className="fas fa-globe mt-2 font-18"></i></div>
            ) : <div className="text-center col-2 stall-info-card py-1 disable-link-color"><i className="fas fa-globe mt-2 font-18"></i></div>}

            {item.stall.providerMobile ? (
              <div className="text-center col-2 stall-info-card py-1 pointer" title={item.stall.providerMobile}><a className="link-color ml-1"><i className="fas fa-phone mt-2 font-18"></i></a></div>
            ) : <div className="text-center col-2 stall-info-card py-1"><a className="disable-link-color ml-1"><i className="fas fa-phone mt-2 font-18"></i></a></div>}

            {item.stall.providerEmail ? (
              <div className="text-center col-2 stall-info-card py-1 pointer"><a className="link-color ml-1" href={'mailto:' + item.stall.providerEmail}><i className="fas fa-envelope mt-2 font-18"></i></a></div>
            ) : <div className="text-center col-2 stall-info-card py-1"><a className="disable-link-color ml-1"><i className="fas fa-envelope mt-2 font-18"></i></a></div>}

            <div className="text-center stall-info-card col-2 py-1 pointer" onClick={navToMessage}><i className="fas fa-paper-plane mt-2 font-18"></i></div>

            <div className="text-center stall-info-card col-2 py-1 pointer" onClick={saveFollow}>{!followItem.isFollow ? <span><img src={notliked} className='mt-2' /></span> : <span><img src={liked} className='mt-2' /></span>}</div>

            <div className="text-center stall-info-card col-2 py-1 pointer" onClick={() => handleShareSlug((item && item.stall && item.stall.slug) || '')}><i className="fas fa-share mt-2 font-18"></i></div>
            <div className="col-2"></div>
          </div>
        </div>

        <div className="col-4 text-right pr-0">
          <span className="pr-4 font-20"><b>Scan to Order </b> <span><ArrowForwardIcon /></span></span>
          <img src={qrcode} alt="Qrcode Image" width="150" height="150" />
        </div>
      </div>

      {/* Drawers for message and login */}
      <Drawer anchor={"right"} classes={{ paper: classes.drawerWidth }} open={openMessage} onClose={toggleDrawerMessage}><MessageContainer stallId={props.stallId} handleMessageClose={handleMessageClose} /></Drawer>

      <Drawer anchor={"right"} classes={{ paper: classes.drawerWidth }} open={openLogin} onClose={toggleDrawerLogin}><LoginContainer handleLoginClose={handleLoginClose} onLoginCallBack={onLoginCallBack} /></Drawer>

      {/* Reviews Sidebar */}
      <StallReviewsSidebar
        open={reviewsSidebarOpen}
        onClose={() => setReviewsSidebarOpen(false)}
        stallId={props.stallId}
        isAdmin={isAdmin}
        onReportClick={openReportDialog}
        onDeleteClick={openDeleteDialog}
        reportedReviews={reportedReviews}
      />

      {/* Report Dialog */}
      <Dialog open={reportDialog.open} onClose={closeReportDialog} aria-labelledby="report-dialog-title" maxWidth="xs" fullWidth fullScreen={fullScreen} disableEnforceFocus disableAutoFocus BackdropProps={{ style: { zIndex: 1405 } }} PaperProps={{ style: { zIndex: 1406, borderRadius: 10 } }} ModalProps={{ container: typeof document !== 'undefined' ? document.body : undefined }}>
        <DialogTitle id="report-dialog-title"><Typography variant="h6">Report Review</Typography></DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>Tell us why you're reporting this review.</Typography>
          <Box mt={1}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" style={{ marginBottom: 8 }}>Select a reason</FormLabel>
              <RadioGroup aria-label="report-reason" name="report-reason-group" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                {ReviewReportReasons.map((reason) => (
                  <FormControlLabel key={reason} value={reason} control={<Radio color="primary"/>} label={<span style={{ fontSize: 14 }}>{reason}</span>} style={{ marginBottom: 6 }} />
                ))}
              </RadioGroup>
            </FormControl>
            {reportReason === 'Other' && (<TextField variant="outlined" label="Describe reason (required)" placeholder="Explain briefly..." value={customMsg} onChange={(e) => setCustomMsg(e.target.value)} fullWidth multiline minRows={3} margin="normal" />)}
            <Divider style={{ marginTop: 8, marginBottom: 8 }} />
            <Typography variant="caption" color="textSecondary">Your report will be reviewed by our moderation team.</Typography>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '12px 16px' }}><Button onClick={closeReportDialog}>Cancel</Button><Button color="secondary" variant="contained" onClick={handleReportSubmit} disabled={reportReason === '' || (reportReason === 'Other' && !customMsg.trim())}>Submit</Button></DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} aria-labelledby="delete-dialog-title" maxWidth="xs" fullWidth fullScreen={fullScreen} BackdropProps={{ style: { zIndex: 1405 } }} PaperProps={{ style: { zIndex: 1406, borderRadius: 10 } }} ModalProps={{ container: typeof document !== 'undefined' ? document.body : undefined }}>
        <DialogTitle id="delete-dialog-title"><Typography variant="h6">Delete Review</Typography></DialogTitle>
        <DialogContent dividers><Typography variant="body1">This will permanently delete this review. Are you sure you want to continue?</Typography></DialogContent>
        <DialogActions><Button onClick={closeDeleteDialog}>Cancel</Button><Button color="secondary" variant="contained" onClick={handleDeleteReview}>Delete</Button></DialogActions>
      </Dialog>
    </div>
  );
};

export default StallHeader;
